import * as React from "react";
import {
  List,
  DataTable,
  FunctionField,
  DateField,
  ReferenceField,
  TextField,
  ImageField,
  Button,
  SelectArrayInput,
  useRecordContext,
  Confirm,
  DataProviderContext,
  useNotify,
  useRefresh,
} from "react-admin";
import { getReportStatusName, getReportTypeName } from "../utils/ReportUtils";
import IconDismiss from "@mui/icons-material/DoubleArrow";
import IconBan from "@mui/icons-material/Block";
import { REPORT_STATUS } from "../enums/ReportStatus";
import { REPORT_TYPES } from "../enums/ReportTypes";
import { SimpleShowLayout } from "../common/SimpleShowLayout";
import { getReviewStatusName } from "../utils/ReviewUtils";

const ReportFilter = () => {
  return [
    <SelectArrayInput
      optionText="name"
      label="Tipo"
      source="type"
      alwaysOn
      choices={[
        {
          id: REPORT_TYPES.IRRELEVANT_CONTENT,
          name: getReportTypeName(REPORT_TYPES.IRRELEVANT_CONTENT),
        },
        {
          id: REPORT_TYPES.SPAM,
          name: getReportTypeName(REPORT_TYPES.SPAM),
        },
        {
          id: REPORT_TYPES.OBSCENE_CONTENT,
          name: getReportTypeName(REPORT_TYPES.OBSCENE_CONTENT),
        },
        {
          id: REPORT_TYPES.HATE_SPEECH,
          name: getReportTypeName(REPORT_TYPES.HATE_SPEECH),
        },
      ]}
    />,
    <SelectArrayInput
      optionText="name"
      label="Estado"
      source="status"
      alwaysOn
      choices={[
        {
          id: REPORT_STATUS.PENDING,
          name: getReportStatusName(REPORT_STATUS.PENDING),
        },
        {
          id: REPORT_STATUS.REVIEWED,
          name: getReportStatusName(REPORT_STATUS.REVIEWED),
        },
        {
          id: REPORT_STATUS.DISMISSED,
          name: getReportStatusName(REPORT_STATUS.DISMISSED),
        },
      ]}
    />,
  ];
};

const ReportPanel = () => (
  <SimpleShowLayout>
    <ReferenceField
      label=""
      reference="reviews"
      source="review._id"
      link="show"
    >
      <FunctionField
        source="_id"
        render={(record) => (
          <Button label="Ir a la Calificación" variant="contained" />
        )}
      />
    </ReferenceField>
    <ReferenceField
      label="Usuario"
      source="review.user"
      reference="users"
      link="show"
    >
      <FunctionField
        source="firstName"
        render={(record) =>
          `${record?.firstName || ""} ${record?.lastName || ""}` || "-"
        }
      />
    </ReferenceField>
    <ReferenceField
      label="Local"
      reference="restaurants"
      source="review.restaurant"
      link="show"
    >
      <TextField source="name" label="Nombre" />
    </ReferenceField>
    <TextField source="review.comment" label="Comentarios" />
    <ImageField source="review.images" src="url" label="Imágenes" />
    <FunctionField
      source="review.status"
      label="Estado"
      render={(record) => getReviewStatusName(record.review?.status)}
    />
    <DateField
      source="review.createdAt"
      label="Fecha"
      showTime={true}
      emptyText="-"
    />
  </SimpleShowLayout>
);

const ReportList = () => {
  const [showConfirmDismiss, setShowConfirmDismiss] = React.useState(false);
  const [showConfirmBan, setShowConfirmBan] = React.useState(false);
  const [reportToAction, setReportToAction] = React.useState();

  const notify = useNotify();
  const refresh = useRefresh();

  const dataProvider = React.useContext(DataProviderContext);

  const DismissButton = () => {
    const record = useRecordContext();

    if (record.status !== REPORT_STATUS.PENDING) return;

    const onDismissPress = (event) => {
      event.stopPropagation();
      setShowConfirmDismiss(true);
      setReportToAction(record);
    };

    return (
      <Button label="Omitir" onClick={onDismissPress}>
        <IconDismiss />
      </Button>
    );
  };

  const BanButton = () => {
    const record = useRecordContext();

    if (record.status !== REPORT_STATUS.PENDING) return;

    const onBanPress = (event) => {
      event.stopPropagation();
      setShowConfirmBan(true);
      setReportToAction(record);
    };

    return (
      <Button label="Bannear" onClick={onBanPress}>
        <IconBan />
      </Button>
    );
  };

  const handleConfirmDismiss = async () => {
    setShowConfirmDismiss(false);
    if (!reportToAction) return;

    try {
      const dismissedReport = await dataProvider.dismissReport("reports", {
        id: reportToAction._id,
      });
      if (dismissedReport) {
        notify("¡Reporte omitido con éxito!", {
          type: "success",
        });
      }
    } catch (error) {
      notify(
        `Ha ocurrido un error al omitir el reporte: ${error?.body?.message}`,
        { type: "error" }
      );
    } finally {
      setReportToAction(null);
      refresh();
    }
  };

  const handleConfirmBan = async () => {
    setShowConfirmBan(false);
    if (!reportToAction) return;

    try {
      const bannedReport = await dataProvider.banReport("reviews", {
        reviewId: reportToAction.review._id,
        reportId: reportToAction._id,
      });
      if (bannedReport) {
        notify("¡Reporte banneado con éxito!", {
          type: "success",
        });
      }
    } catch (error) {
      notify(
        `Ha ocurrido un error al bannear el reporte: ${error?.body?.message}`,
        { type: "error" }
      );
    } finally {
      setReportToAction(null);
      refresh();
    }
  };

  const handleDialogClose = () => {
    setReportToAction(null);
    setShowConfirmDismiss(false);
    setShowConfirmBan(false);
  };

  return (
    <List
      title="Reportes"
      filters={ReportFilter()}
      sort={{ field: "createdAt", order: "DESC" }}
      exporter={false}
      filterDefaultValues={{
        status: [REPORT_STATUS.PENDING],
      }}
    >
      <DataTable
        rowClick="expand"
        bulkActionButtons={false}
        expand={<ReportPanel />}
        expandSingle
      >
        <DataTable.Col source="author" label="Usuario">
          <ReferenceField
            label="Usuario"
            source="author"
            reference="users"
            link="show"
          >
            <FunctionField
              source="firstName"
              render={(record) =>
                `${record?.firstName || ""} ${record?.lastName || ""}` || "-"
              }
            />
          </ReferenceField>
        </DataTable.Col>

        <DataTable.Col source="type" label="Tipo">
          <FunctionField
            source="type"
            label="Tipo"
            render={(record) => getReportTypeName(record.type)}
          />
        </DataTable.Col>

        <DataTable.Col source="comment" label="Comentarios">
          <TextField source="comment" label="Comentarios" emptyText="-" />
        </DataTable.Col>

        <DataTable.Col source="status" label="Estado">
          <FunctionField
            source="status"
            label="Estado"
            render={(record) => getReportStatusName(record.status)}
          />
        </DataTable.Col>

        <DataTable.Col source="createdAt" label="Fecha">
          <DateField
            source="createdAt"
            label="Fecha"
            showTime={true}
            emptyText="-"
          />
        </DataTable.Col>
        <DataTable.Col>
          <DismissButton />
          <BanButton />
        </DataTable.Col>
      </DataTable>
      <Confirm
        isOpen={showConfirmDismiss}
        title={`Omitir reporte`}
        content="¿Estás seguro de omitir este reporte?"
        onConfirm={handleConfirmDismiss}
        onClose={handleDialogClose}
      />
      <Confirm
        isOpen={showConfirmBan}
        title={`Bannear reporte`}
        content="¿Estás seguro de bannear este reporte?"
        onConfirm={handleConfirmBan}
        onClose={handleDialogClose}
      />
    </List>
  );
};

export default ReportList;
